import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { MessageSquareText, Mail, RefreshCw, Trash2, Plus, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { StatusPill } from "@/components/bits";

function Card({ icon: Icon, title, subtitle, right, children, testId }) {
  return (
    <section className="surface p-5" data-testid={testId}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-[#181C24] text-zinc-300"><Icon className="h-4 w-4" /></span>
          <div>
            <h2 className="font-display text-lg font-semibold text-zinc-100">{title}</h2>
            <p className="text-xs text-zinc-500">{subtitle}</p>
          </div>
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

function OpenPhoneCard({ source, config, refresh }) {
  const [apiKey, setApiKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [watched, setWatched] = useState(source?.watched_ids || []);
  useEffect(() => { setWatched(source?.watched_ids || []); }, [source?.id, source?.watched_ids]);

  const connect = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await api.post("/sources/openphone/connect", { api_key: apiKey });
      toast.success(`OpenPhone connected · watching ${r.data.phone_numbers.length} number(s)`);
      setApiKey(""); refresh();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const saveNumbers = async () => {
    setBusy(true);
    try { await api.patch("/sources/openphone/numbers", { watched_ids: watched }); toast.success("Watched numbers updated"); refresh(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect OpenPhone? Incoming texts will no longer be read.")) return;
    setBusy(true);
    try { await api.delete("/sources/openphone"); toast.success("OpenPhone disconnected"); refresh(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Card icon={MessageSquareText} title="OpenPhone" subtitle="Texts to your shared number(s), delivered instantly via webhook"
      right={<StatusPill status={source ? source.status : "disconnected"} data-testid="openphone-status-pill" />} testId="openphone-card">
      {!source ? (
        <form onSubmit={connect} className="flex flex-col gap-3" data-testid="openphone-connect-form">
          <p className="text-sm text-zinc-400">
            Paste an API key from OpenPhone (now “Quo”) → <span className="text-zinc-200">Workspace settings → API</span>. Requires Owner/Admin and a plan with API access.
            The hub will register itself for incoming texts at <code className="font-mono text-xs text-zinc-300">{config?.openphone_webhook_url}</code>.
          </p>
          <div className="flex gap-2">
            <Input value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="OpenPhone API key" type="password" required className="bg-[#0B0D11] font-mono" data-testid="openphone-api-key-input" />
            <Button type="submit" disabled={busy || !apiKey} data-testid="openphone-connect-button">{busy ? "Connecting…" : "Connect"}</Button>
          </div>
        </form>
      ) : (
        <div className="flex flex-col gap-4">
          {source.status_detail && <p className="text-sm text-amber-400" data-testid="openphone-status-detail">{source.status_detail}</p>}
          <div className="grid gap-1 text-xs text-zinc-500 sm:grid-cols-3">
            <p>Connected by <span className="text-zinc-300">{source.connected_by}</span></p>
            <p>Webhook <span className="font-mono text-zinc-300">{source.webhook_id || "—"}</span></p>
            <p>Last text {source.last_event_at ? <span className="text-zinc-300">{timeAgo(source.last_event_at)}</span> : <span className="text-zinc-600">none yet</span>}</p>
          </div>
          <div>
            <p className="eyebrow mb-2">Phone numbers watched {watched.length === 0 && <span className="normal-case tracking-normal text-zinc-500">· all</span>}</p>
            <ul className="divide-y divide-[#1B202A] rounded-md border border-[#242A36]">
              {source.phone_numbers.map((n) => {
                const on = watched.length === 0 || watched.includes(n.id);
                return (
                  <li key={n.id} className="flex items-center gap-3 px-3 py-2" data-testid={`openphone-number-${n.id}`}>
                    <Checkbox checked={on} onCheckedChange={(v) => {
                      const all = source.phone_numbers.map((x) => x.id);
                      const base = watched.length === 0 ? all : watched;
                      const next = v ? [...new Set([...base, n.id])] : base.filter((x) => x !== n.id);
                      setWatched(next.length === all.length ? [] : next);
                    }} data-testid={`openphone-number-toggle-${n.id}`} />
                    <span className="font-mono text-sm text-zinc-200">{n.formatted || n.number}</span>
                    <span className="text-xs text-zinc-500">{n.name}</span>
                  </li>
                );
              })}
            </ul>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" onClick={saveNumbers} disabled={busy} data-testid="openphone-save-numbers-button">Save numbers</Button>
            <Button size="sm" variant="ghost" onClick={disconnect} disabled={busy} className="text-rose-400 hover:text-rose-300" data-testid="openphone-disconnect-button"><Trash2 className="h-3.5 w-3.5" /> Disconnect</Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function GmailCard({ sources, config, refresh }) {
  const [busy, setBusy] = useState("");
  const [mailbox, setMailbox] = useState("");
  const connect = async (e) => {
    e?.preventDefault();
    setBusy("connect");
    try { const r = await api.get("/sources/gmail/connect-url", { params: { email: mailbox } }); window.location.href = r.data.url; }
    catch (err) { toast.error(errMsg(err)); setBusy(""); }
  };
  const checkNow = async (id) => {
    setBusy(id);
    try { const r = await api.post(`/sources/gmail/${id}/check-now`); toast.success(r.data.codes_found ? `${r.data.codes_found} new code(s) found` : "Checked — nothing new"); refresh(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(""); }
  };
  const disconnect = async (s) => {
    if (!window.confirm(`Disconnect ${s.email}?`)) return;
    setBusy(s.id);
    try { await api.delete(`/sources/gmail/${s.id}`); toast.success("Mailbox disconnected"); refresh(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(""); }
  };

  return (
    <Card icon={Mail} title="Gmail mailboxes" subtitle="Read-only. Each connected inbox is checked every ~15 seconds." testId="gmail-card">
      {config?.gmail_configured && (
        <form onSubmit={connect} className="mb-4 flex flex-wrap items-center gap-2 rounded-md border border-[#242A36] bg-[#0E1117] p-3" data-testid="gmail-connect-form">
          <Input type="email" value={mailbox} onChange={(e) => setMailbox(e.target.value)} placeholder="mailbox to connect, e.g. admin@totalqualityinsurance.com" className="min-w-[260px] flex-1 bg-[#0B0D11]" data-testid="gmail-mailbox-input" />
          <Button type="submit" disabled={busy === "connect"} className="gap-2" data-testid="gmail-connect-button"><Plus className="h-4 w-4" /> Connect mailbox</Button>
          <p className="w-full text-xs text-zinc-500">Google will ask you to pick an account — choose the <span className="text-zinc-300">work</span> account (not a personal @gmail.com; those are blocked because the app is organisation-only). Typing the address above pre-selects it.</p>
        </form>
      )}
      {!config?.gmail_configured && (
        <div className="mb-4 rounded-md border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-200" data-testid="gmail-setup-notice">
          <p className="font-semibold">One-time Google setup needed before mailboxes can be connected</p>
          <ol className="mt-2 list-decimal space-y-1 pl-5 text-amber-100/90">
            <li>Go to <a className="underline" href="https://console.cloud.google.com" target="_blank" rel="noreferrer">console.cloud.google.com</a> → create a project (e.g. “Passcode Hub”).</li>
            <li>APIs &amp; Services → Library → enable <b>Gmail API</b>.</li>
            <li>OAuth consent screen → User type <b>Internal</b> (Workspace) → app name “Passcode Hub” → add scope <code className="font-mono text-xs">gmail.readonly</code>.</li>
            <li>Credentials → Create OAuth client ID → <b>Web application</b> → Authorized redirect URI:<br />
              <code className="font-mono text-xs text-amber-50" data-testid="gmail-redirect-uri">{config?.gmail_redirect_uri}</code></li>
            <li>Send the Client ID and Client Secret to be added to the hub as <code className="font-mono text-xs">GOOGLE_CLIENT_ID</code> / <code className="font-mono text-xs">GOOGLE_CLIENT_SECRET</code>.</li>
          </ol>
        </div>
      )}
      {sources.length === 0 ? (
        <p className="text-sm text-zinc-500" data-testid="gmail-empty">No mailboxes connected. Connect each shared inbox (e.g. admin@yourcompany.com) one at a time; you'll sign in to that Google account and grant read-only access.</p>
      ) : (
        <ul className="divide-y divide-[#1B202A] rounded-md border border-[#242A36]">
          {sources.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5" data-testid={`gmail-mailbox-${s.id}`}>
              <div className="min-w-0 flex-1">
                <p className="truncate font-mono text-sm text-zinc-200">{s.email}</p>
                <p className="text-xs text-zinc-500">
                  checked {s.last_checked_at ? timeAgo(s.last_checked_at) : "never"} · last code {s.last_event_at ? timeAgo(s.last_event_at) : "none yet"}
                  {s.status_detail && <span className="ml-2 text-amber-400">{s.status_detail}</span>}
                </p>
              </div>
              <StatusPill status={s.status} data-testid={`gmail-status-${s.id}`} />
              <Button size="sm" variant="ghost" onClick={() => checkNow(s.id)} disabled={busy === s.id} data-testid={`gmail-check-${s.id}`}><RefreshCw className="h-3.5 w-3.5" /> Check now</Button>
              <Button size="sm" variant="ghost" onClick={() => disconnect(s)} disabled={busy === s.id} className="text-rose-400 hover:text-rose-300" data-testid={`gmail-disconnect-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export default function Sources() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const sources = useQuery({ queryKey: ["sources"], queryFn: () => api.get("/sources").then((r) => r.data), refetchInterval: 15000, staleTime: 0 });
  const config = useQuery({ queryKey: ["config"], queryFn: () => api.get("/config").then((r) => r.data) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["sources"] });

  useEffect(() => {
    const g = params.get("gmail");
    if (!g) return;
    if (g === "connected") toast.success(`Mailbox ${params.get("email")} connected`);
    else toast.error(params.get("reason") || "Gmail connection failed");
    setParams({}, { replace: true });
  }, [params, setParams]);

  const list = sources.data || [];
  const op = list.find((s) => s.type === "openphone");
  const gm = list.filter((s) => s.type === "gmail");

  return (
    <div className="rise flex flex-col gap-6">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-100">Sources</h1>
        <p className="mt-1 text-sm text-zinc-400">Where codes come from. Secrets stay on the hub; members never see them.</p>
      </div>
      <OpenPhoneCard source={op} config={config.data} refresh={refresh} />
      <GmailCard sources={gm} config={config.data} refresh={refresh} />
      <p className="flex items-center gap-1 text-xs text-zinc-600"><ExternalLink className="h-3 w-3" /> Authenticator-app (TOTP) codes are out of scope — only texted and emailed codes are handled.</p>
    </div>
  );
}
